import { useState, useRef, useEffect, useMemo } from "react";
import {
  RiLineChartLine,
  RiMenuLine,
  RiToolsLine,
  RiMoneyDollarCircleLine,
  RiGroupLine,
  RiArrowDownSLine,
  RiUserStarLine,
  RiBookLine,
  RiSettings3Line,
  RiMapPinLine,
  RiFileListLine,
  RiPriceTag3Line,
  RiVipDiamondLine,
  RiTeamLine,
  RiCoupon3Line,
  RiPencilRuler2Line,
  RiDraftLine,
} from "react-icons/ri";
import { Link, useLocation } from "react-router-dom";
import { Tooltip } from "@mui/material";
import { i18n } from "../../../localization/i18n";
import {
  MenuItemContainer,
  MenuButton,
  SubMenuWrapper,
  SubMenuItem,
} from "../styles";

interface MenuListProps {
  extendedMode: boolean;
  setExtendedMode?: (val: boolean) => void;
}

interface SubMenuItemType {
  title: string;
  path: string;
  icon?: React.ReactNode;
}

interface MenuConfig {
  id: string;
  title: string;
  icon: React.ReactNode;
  path?: string;
  submenus?: SubMenuItemType[];
}

export const AdminMenuList = ({ extendedMode, setExtendedMode }: MenuListProps) => {
  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);
  const location = useLocation();

  const handleMenuClick = (menu: MenuConfig) => {
    if (menu.submenus) {
      if (!extendedMode && setExtendedMode) {
        setExtendedMode(true);
        setOpenSubmenu(menu.id); // Força a abrir o submenu ignorando o estado anterior se a barra estava fechada
      } else {
        setOpenSubmenu((prev) => (prev === menu.id ? null : menu.id)); // Comportamento normal de toggle se a barra já estava aberta
      }
    } else {
      setOpenSubmenu(null);
    }
  };

  const tooltipProps = {
    tooltip: {
      sx: {
        backgroundColor: "var(--dark-gray)",
        color: "var(--white)",
        fontSize: "14px",
        fontWeight: "500",
        padding: "8px 14px",
        borderRadius: "6px",
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)"
      }
    },
    arrow: {
      sx: {
        color: "var(--dark-gray)"
      }
    }
  };

  const menuConfig: MenuConfig[] = useMemo(
    () => [
      {
        id: "reports",
        title: i18n.t("sidebar.reports"),
        icon: <RiLineChartLine size={24} />,
        path: "/admin/dashboard",
      },
      {
        id: "requests",
        title: i18n.t("sidebar.requests"),
        icon: <RiMenuLine size={24} />,
        path: "/admin/orders",
      },
      {
        id: "customers",
        title: "Clientes",
        icon: <RiUserStarLine size={24} />,
        submenus: [
          {
            title: "Licenciados",
            path: "/customers/licensees",
            icon: <RiUserStarLine size={24} />,
          },
          {
            title: "Fixiters",
            path: "/customers/fixiters",
            icon: <RiGroupLine size={24} />,
          },
          {
            title: "Propostas",
            path: "/customers/proposals",
            icon: <RiDraftLine size={24} />,
          },
        ],
      },
      {
        id: "financial",
        title: "Financeiro",
        icon: <RiMoneyDollarCircleLine size={24} />,
        submenus: [
          {
            title: i18n.t("bills.bills"),
            path: "/admin/bills",
            icon: <RiFileListLine size={24} />,
          },
          {
            title: "Tabelas de Preço",
            path: "/admin/price-tables",
            icon: <RiPriceTag3Line size={24} />,
          },
          {
            title: i18n.t("sidebar.plans"),
            path: "/admin/plans",
            icon: <RiVipDiamondLine size={24} />,
          },
        ],
      },
      {
        id: "courses",
        title: "Cursos",
        icon: <RiBookLine size={24} />,
        submenus: [
          {
            title: "Turmas",
            path: "/admin/courses/classes",
            icon: <RiTeamLine size={24} />,
          },
          {
            title: "Cupons",
            path: "/admin/courses/coupons",
            icon: <RiCoupon3Line size={24} />,
          },
        ],
      },
      {
        id: "users",
        title: "Usuários",
        icon: <RiGroupLine size={24} />,
        submenus: [
          {
            title: i18n.t("sidebar.designers"),
            path: "/users/creators",
            icon: <RiPencilRuler2Line size={24} />,
          },
        ],
      },
      {
        id: "tools",
        title: "Ferramentas",
        icon: <RiSettings3Line size={24} />,
        submenus: [
          {
            title: "Localizador",
            path: "/admin/tools/locator",
            icon: <RiMapPinLine size={24} />,
          },
        ],
      },
      {
        id: "configs",
        title: "Configurações",
        icon: <RiToolsLine size={24} />,
        path: "/configs",
      },
    ],
    []
  );

  useEffect(() => {
    const activeMenu = menuConfig.find(
      (menu) => menu.submenus && menu.submenus.some((sub) => location.pathname.includes(sub.path))
    );
    if (activeMenu) {
      setOpenSubmenu(activeMenu.id);
    }
  }, [location.pathname, menuConfig]);

  // Helper component to calculate heights for CSS transitions
  const AccordionSubmenu = ({
    menu,
    isOpen,
  }: {
    menu: MenuConfig;
    isOpen: boolean;
  }) => {
    const contentRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState(0);

    useEffect(() => {
      if (isOpen && contentRef.current) {
        setHeight(contentRef.current.scrollHeight);
      } else {
        setHeight(0);
      }
    }, [isOpen]);

    return (
      <SubMenuWrapper isOpen={isOpen} contentHeight={height}>
        <div ref={contentRef}>
          {menu.submenus?.map((sub) => {
            const isSubActive = location.pathname.includes(sub.path);
            return (
              <SubMenuItem key={sub.path} isActive={isSubActive}>
                <Link to={sub.path}>
                  {sub.icon && <span className="sub-icon">{sub.icon}</span>}
                  {sub.title}
                </Link>
              </SubMenuItem>
            );
          })}
        </div>
      </SubMenuWrapper>
    );
  };

  return (
    <>
      {menuConfig.map((menu) => {
        const isParentActive =
          (menu.path && location.pathname.includes(menu.path)) ||
          (menu.submenus &&
            menu.submenus.some((sub) => location.pathname.includes(sub.path)));
            
        const isSubmenuOpen = openSubmenu === menu.id && extendedMode;

        const button = (
          <Tooltip 
            title={extendedMode ? "" : menu.title} 
            placement="right" 
            arrow 
            disableHoverListener={extendedMode}
            componentsProps={tooltipProps}
          >
            <span style={{ display: 'block' }}>
              <MenuButton
                isActive={isParentActive}
                extendedMode={extendedMode}
                onClick={() => handleMenuClick(menu)}
              >
                <div className="content">
                  {menu.icon}
                  <span>{menu.title}</span>
                </div>
                {menu.submenus && (
                  <RiArrowDownSLine
                    className="arrow"
                    size={20}
                    style={{
                      transform: isSubmenuOpen ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                )}
              </MenuButton>
            </span>
          </Tooltip>
        );

        return (
          <MenuItemContainer key={menu.id}>
            {menu.path ? (
              <Link to={menu.path}>{button}</Link>
            ) : (
              <div>{button}</div>
            )}
            
            {menu.submenus && (
              <AccordionSubmenu menu={menu} isOpen={isSubmenuOpen} />
            )}
          </MenuItemContainer>
        );
      })}
    </>
  );
};
